import request from '@/utils/request'
import { getCommunityId } from '@/api/community/communityApi'

/**
 * 查询流程模板列表
 * @description 获取报修流程模板列表数据
 * @api GET /pms.queryFlowTemplate
 * @param {Object} params - 查询参数
 * @param {number} [params.id] - 模板ID(更新时传入)，可选
 * @param {string} params.templateCode - 模板编码，必填
 * @param {string} params.templateName - 模板名称，必填
 * @param {string} params.isEnable - 是否启用，必填
 * @returns {Promise<{data: Array, total: number}>} 返回模板列表和总数
 */
export function listRepairFlowTemplates(params) {
  return new Promise((resolve, reject) => {
    request({
      url: '/pms.queryFlowTemplate',
      method: 'get',
      params: {
        ...params,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      if (res.code == 0) {
        resolve({
          data: res.data,
          total: res.total
        })
      } else {
        reject(new Error(res.msg || '获取报修流程模板列表失败'))
      }
    }).catch(error => {
      reject(error)
    })
  })
}

/**
 * 查询流程节点配置
 * @description 根据模板ID查询流程节点列表
 * @api GET /pms.queryFlowNodeConfig
 * @param {Object} params - 查询参数
 * @param {number} params.templateId - 模板ID，必填
 * @param {string} [params.page] - 页码，可选
 * @param {string} [params.row] - 每页条数，可选
 * @returns {Promise<{data: Array}>} 返回节点列表
 */
export function queryFlowNodeConfig(params) {
  return new Promise((resolve, reject) => {
    request({
      url: '/pms.queryFlowNodeConfig',
      method: 'get',
      params: {
        ...params,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      if (res.code == 0) {
        resolve({
          data: res.data || []
        })
      } else {
        reject(new Error(res.msg || '获取节点配置失败'))
      }
    }).catch(error => {
      reject(error)
    })
  })
}

/**
 * 新增/修改报修流程模板
 * @description 保存或更新报修流程模板
 * @api POST /pms.saveOrUpdateFlowTemplate
 * @param {Object} data - 模板数据
 * @param {number} [data.id] - 模板ID（更新时传入）
 * @param {string} data.templateName - 模板名称，必填
 * @param {string} data.isEnable - 是否启用，必填（1-启用，0-禁用）
 * @returns {Promise<Object>} 返回操作结果
 */
export function saveOrUpdateFlowTemplate(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/pms.saveOrUpdateFlowTemplate',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      if (res.code == 0) {
        resolve(res)
      } else {
        reject(new Error(res.msg || '保存失败'))
      }
    }).catch(error => {
      reject(error)
    })
  })
}

/**
 * 新增报修流程模板
 * @description 保存新的报修流程模板
 * @param {Object} data - 模板数据
 * @param {string} data.templateCode - 模板编码
 * @param {string} data.templateName - 模板名称
 * @param {string} data.isEnable - 是否启用
 * @returns {Promise<Object>} 返回操作结果
 */
export function addRepairFlowTemplate(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/repairFlowTemplate.saveRepairFlowTemplate',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      if (res.code == 0) {
        resolve(res)
      } else {
        reject(new Error(res.msg || '新增失败'))
      }
    }).catch(error => {
      reject(error)
    })
  })
}

/**
 * 修改报修流程模板
 * @description 更新已有的报修流程模板
 * @param {Object} data - 模板数据
 * @param {number} data.id - 模板ID
 * @param {string} data.templateCode - 模板编码
 * @param {string} data.templateName - 模板名称
 * @param {string} data.isEnable - 是否启用
 * @returns {Promise<Object>} 返回操作结果
 */
export function updateRepairFlowTemplate(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/repairFlowTemplate.updateRepairFlowTemplate',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      if (res.code == 0) {
        resolve(res)
      } else {
        reject(new Error(res.msg || '修改失败'))
      }
    }).catch(error => {
      reject(error)
    })
  })
}

/**
 * 删除报修流程模板
 * @description 根据模板ID删除报修流程模板
 * @api POST /pms.deleteFlowTemplate
 * @param {number} id - 模板ID
 * @returns {Promise<Object>} 返回操作结果
 */
export function deleteRepairFlowTemplate(id) {
  return new Promise((resolve, reject) => {
    request({
      url: '/pms.deleteFlowTemplate',
      method: 'post',
      data: {
        id,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      if (res.code == 0) {
        resolve(res)
      } else {
        reject(new Error(res.msg || '删除失败'))
      }
    }).catch(error => {
      reject(error)
    })
  })
}

/**
 * 保存或更新流程节点配置
 * @description 新增或修改流程节点
 * @api POST /pms.saveOrUpdateFlowNodeConfig
 * @param {Object} data - 节点数据
 * @param {number} [data.id] - 节点ID（更新时传入）
 * @param {number} data.templateId - 关联模板ID
 * @param {string} [data.nodeName] - 节点名称
 * @param {number} [data.sortOrder] - 全局顺序 (1, 2, 3...)
 * @param {string} [data.semanticTag] - 业务语义标签 (START, SURVEY, APPROVAL, WORK, END)
 * @param {number} [data.handlerType] - 处理人类型: 1-指定角色, 2-指定部门负责人, 3-发起人, 4-工单处理人
 * @param {string} [data.handlerKey] - 关联值 (角色Key等)
 * @param {number} [data.canReject] - 是否允许驳回 (1-是 0-否)
 * @param {number} [data.rejectTarget] - 驳回目标：1-上一步, 0-直接回到发起人
 * @returns {Promise<Object>} 返回操作结果
 */
export function saveOrUpdateFlowNodeConfig(data) {
  return new Promise((resolve, reject) => {
    request({
      url: '/pms.saveOrUpdateFlowNodeConfig',
      method: 'post',
      data: {
        ...data,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      if (res.code == 0) {
        resolve(res)
      } else {
        reject(new Error(res.msg || '保存节点失败'))
      }
    }).catch(error => {
      reject(error)
    })
  })
}

/**
 * 删除流程节点配置
 * @description 根据节点ID删除流程节点
 * @api POST /pms.deleteFlowNodeConfig
 * @param {number} id - 节点ID
 * @returns {Promise<Object>} 返回操作结果
 */
export function deleteFlowNodeConfig(id) {
  return new Promise((resolve, reject) => {
    request({
      url: '/pms.deleteFlowNodeConfig',
      method: 'post',
      data: {
        id,
        communityId: getCommunityId()
      }
    }).then(response => {
      const res = response.data
      if (res.code == 0) {
        resolve(res)
      } else {
        reject(new Error(res.msg || '删除节点失败'))
      }
    }).catch(error => {
      reject(error)
    })
  })
}
